import { create } from "zustand";
import { t } from "../i18n";
import { useUi } from "./ui";

/** R9-A8 / A13: a delete waits behind an Undo toast. The screen changes at once; the request
 *  goes when the toast does (8 s), unless Undo was pressed. Leaving sends what's waiting at
 *  once: the page hidden (an iPad backgrounding the app often never fires `pagehide`) or
 *  closed (both `keepalive`), and signing out (`flushWaiting`, before the session ends). While
 *  a key waits, lists hide what it names (`useWaiting`), whatever a refetch brings back. */
type Waiting = {
  commit: (keepalive: boolean) => Promise<void>;
  onFail: (e: unknown) => void;
  toastId: number;
  hides: string[];
};

const waiting = new Map<string, Waiting>();

export const useWaiting = create<{ keys: ReadonlySet<string> }>(() => ({ keys: new Set() }));

function mark(key: string, on: boolean): void {
  useWaiting.setState((s) => {
    const keys = new Set(s.keys);
    if (on) keys.add(key);
    else keys.delete(key);
    return { keys };
  });
}

export function deferWithUndo({
  key,
  hides = [key],
  text,
  commit,
  undo,
  onFail,
}: {
  key: string;
  /** The keys lists hide while it waits (several chats at once, R10-Q1); the key itself by
   *  default. */
  hides?: string[];
  text: string;
  /** Send the request. */
  commit: (keepalive: boolean) => Promise<void>;
  /** Put the screen back as it was: Undo pressed (pressed), or a failed request, maybe long
   *  after the toast went (R10.8: only Undo may take the person anywhere). */
  undo: (pressed: boolean) => void;
  onFail?: (e: unknown) => void;
}): void {
  ensureFlushOnLeave();
  for (const k of hides) mark(k, true);
  const toastId = useUi.getState().toast(text, {
    action: {
      label: t("common.undo"),
      run: () => {
        if (!waiting.delete(key)) return;
        for (const k of hides) mark(k, false);
        undo(true);
      },
    },
    onExpire: () => void send(key, false),
  });
  waiting.set(key, {
    commit,
    toastId,
    hides,
    onFail: (e) => {
      undo(false);
      onFail?.(e);
    },
  });
}

function send(key: string, keepalive: boolean): Promise<void> {
  const w = waiting.get(key);
  if (!w) return Promise.resolve();
  waiting.delete(key);
  // Sent early (leaving, signing out): its Undo can't take it back any more.
  useUi.getState().dropToast(w.toastId);
  return w
    .commit(keepalive)
    .catch((e: unknown) => {
      if (!keepalive) w.onFail(e);
    })
    .finally(() => {
      for (const k of w.hides) mark(k, false);
    });
}

/** Send everything waiting now and wait for it (before signing out). */
export async function flushWaiting(): Promise<void> {
  await Promise.all([...waiting.keys()].map((key) => send(key, false)));
}

let listening = false;

function ensureFlushOnLeave(): void {
  if (listening || typeof window === "undefined") return;
  listening = true;
  const leave = () => {
    for (const key of [...waiting.keys()]) void send(key, true);
  };
  window.addEventListener("pagehide", leave);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") leave();
  });
}
